import {
  PartnerProviderLogsFilters,
  PartnerProviderLogsResponse,
} from "@/types/partner-provider-log";
import {
  PartnerOrdersSummaryQuery,
  PartnerRevenueAnalyticsApiResponse,
  PartnerRevenueAnalyticsQuery,
} from "@/types/partner-revenue-analytics";
import type { HotelPartnerOrdersSummary } from "@/types/hotelService";
import {
  PartnerDestination,
  PartnerSettings,
  UpdatePartnerSettingsRequest,
  PartnerServicesListResponse,
  PartnerServiceFilterOptions,
  PartnerServicesFilters,
} from "@/types/partner-settings";
import { BaseService } from "./BaseService";

class PartnerSettingsService extends BaseService {
  async getPartnerSettings(hotelId: string): Promise<PartnerSettings> {
    return this.get<PartnerSettings>(`/hotels/${hotelId}/partners/settings`);
  }

  async getPartnerOrdersSummary(
    hotelId: string,
    query: PartnerOrdersSummaryQuery,
  ): Promise<HotelPartnerOrdersSummary> {
    return this.get<HotelPartnerOrdersSummary>(
      `/hotels/${hotelId}/partners/orders/summary`,
      query,
    );
  }

  async getPartnerRevenueAnalytics(
    hotelId: string,
    query: PartnerRevenueAnalyticsQuery,
  ): Promise<PartnerRevenueAnalyticsApiResponse> {
    return this.get<PartnerRevenueAnalyticsApiResponse>(
      `/hotels/${hotelId}/partners/orders/revenue-analytics`,
      query,
    );
  }

  async searchDestinations(
    hotelId: string,
    query: string,
  ): Promise<PartnerDestination[]> {
    return this.get<PartnerDestination[]>(
      `/hotels/${hotelId}/partners/destinations/search`,
      { query },
    );
  }

  async triggerSync(hotelId: string): Promise<void> {
    return this.post<void>(`/hotels/${hotelId}/partners/sync`, {});
  }

  async getPartnerLogs(
    hotelId: string,
    filters?: PartnerProviderLogsFilters,
  ): Promise<PartnerProviderLogsResponse> {
    return this.get<PartnerProviderLogsResponse>(
      `/hotels/${hotelId}/partners/logs`,
      filters,
    );
  }

  async updatePartnerSettings(
    hotelId: string,
    data: UpdatePartnerSettingsRequest,
  ): Promise<PartnerSettings> {
    return this.put<PartnerSettings>(
      `/hotels/${hotelId}/partners/settings`,
      data,
    );
  }

  async getPartnerServices(
    hotelId: string,
    filters?: PartnerServicesFilters,
  ): Promise<PartnerServicesListResponse> {
    return this.get<PartnerServicesListResponse>(
      `/hotels/${hotelId}/partners/services`,
      filters,
    );
  }

  async getPartnerServicesFilterOptions(
    hotelId: string,
  ): Promise<PartnerServiceFilterOptions> {
    return this.get<PartnerServiceFilterOptions>(
      `/hotels/${hotelId}/partners/services/filter-options`,
    );
  }
}

export const partnerSettingsService = new PartnerSettingsService();
